import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { Client } from '../../types/crm';
import { idsMatch } from '../../utils/crmHelpers';
import { bindTypeaheadReposition, computeFixedTypeaheadStyle } from '../../utils/typeaheadPosition';

export function clientTypeaheadLabel(client: Client): string {
  return `${client.company_name} (${client.code})`;
}

type AccountTypeaheadProps = {
  clients: Client[];
  value: string;
  onChange: (clientId: string) => void;
  placeholder?: string;
  disabled?: boolean;
  allowNone?: boolean;
  noneLabel?: string;
  maxVisible?: number;
  /** Allow typing a new company name (not in the list). */
  allowCreate?: boolean;
  createOptionLabel?: (name: string) => string;
  onNewAccountName?: (name: string) => void;
  /** Shown in the input when no client is selected but a new name was entered. */
  newAccountDraft?: string;
};

export function AccountTypeahead({
  clients,
  value,
  onChange,
  placeholder = 'Search by account name or code',
  disabled = false,
  allowNone = false,
  noneLabel = '— None —',
  maxVisible = 120,
  allowCreate = false,
  createOptionLabel = (name) => `New account: ${name}`,
  onNewAccountName,
  newAccountDraft = '',
}: AccountTypeaheadProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [listStyle, setListStyle] = useState<React.CSSProperties>({});
  /** While true, do not overwrite the input from selected / draft (user is typing). */
  const isEditingRef = useRef(false);
  const justCommittedRef = useRef(false);
  const valueRef = useRef(value);
  const queryRef = useRef(query);
  const newAccountDraftRef = useRef(newAccountDraft);

  valueRef.current = value;
  queryRef.current = query;
  newAccountDraftRef.current = newAccountDraft;

  const selected = useMemo(
    () => clients.find((c) => idsMatch(c.id, value)),
    [clients, value],
  );

  useEffect(() => {
    if (isEditingRef.current) return;
    if (selected) {
      setQuery(clientTypeaheadLabel(selected));
    } else if (newAccountDraft.trim()) {
      setQuery(newAccountDraft.trim());
    } else {
      setQuery('');
    }
  }, [selected?.id, selected?.company_name, selected?.code, newAccountDraft]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const sorted = [...clients].sort((a, b) => a.company_name.localeCompare(b.company_name));
    if (!q) return sorted.slice(0, maxVisible);
    return sorted
      .filter((c) => {
        const pool = `${c.company_name} ${c.code} ${c.client_type ?? ''}`.toLowerCase();
        return pool.includes(q);
      })
      .slice(0, maxVisible);
  }, [clients, query, maxVisible]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  const updateListPosition = () => {
    const rect = inputRef.current?.getBoundingClientRect();
    if (!rect) return;
    setListStyle(
      computeFixedTypeaheadStyle(rect, {
        minWidth: Math.max(rect.width, 280),
        maxWidth: 480,
        maxHeightCap: 280,
        gap: 2,
        zIndex: 4200,
      }),
    );
  };

  useEffect(() => {
    if (!open) return;
    updateListPosition();
    return bindTypeaheadReposition(updateListPosition);
  }, [open, query]);

  const trimmedQuery = query.trim();
  const queryMatchesAnyClient = useMemo(
    () =>
      trimmedQuery
        ? clients.some(
            (c) => clientTypeaheadLabel(c).toLowerCase() === trimmedQuery.toLowerCase(),
          )
        : false,
    [clients, trimmedQuery],
  );
  const queryMatchesClient = useMemo(
    () =>
      trimmedQuery
        ? filtered.some(
            (c) => clientTypeaheadLabel(c).toLowerCase() === trimmedQuery.toLowerCase(),
          )
        : false,
    [filtered, trimmedQuery],
  );
  const showCreateOption =
    allowCreate && Boolean(trimmedQuery) && !queryMatchesAnyClient && !queryMatchesClient;

  function commitNewAccountName(name: string) {
    const trimmed = name.trim();
    if (!trimmed || !onNewAccountName) return;
    justCommittedRef.current = true;
    onChange('');
    onNewAccountName(trimmed);
    setQuery(trimmed);
    isEditingRef.current = false;
    setOpen(false);
  }

  function selectClient(client: Client | null) {
    justCommittedRef.current = true;
    isEditingRef.current = false;
    if (!client) {
      onChange('');
      setQuery('');
      onNewAccountName?.('');
    } else {
      onChange(String(client.id));
      setQuery(clientTypeaheadLabel(client));
      onNewAccountName?.('');
    }
    setOpen(false);
  }

  function settleBlur() {
    if (justCommittedRef.current) {
      justCommittedRef.current = false;
      return;
    }

    const currentValue = valueRef.current;
    const currentQuery = queryRef.current.trim();
    const currentDraft = newAccountDraftRef.current.trim();
    const currentSelected = clients.find((c) => idsMatch(c.id, currentValue));

    isEditingRef.current = false;

    if (currentSelected) {
      setQuery(clientTypeaheadLabel(currentSelected));
      return;
    }

    if (!currentQuery) {
      onChange('');
      onNewAccountName?.('');
      setQuery('');
      return;
    }

    if (allowCreate && !clients.some(
      (c) => clientTypeaheadLabel(c).toLowerCase() === currentQuery.toLowerCase(),
    )) {
      commitNewAccountName(currentQuery);
      return;
    }

    if (currentDraft) {
      setQuery(currentDraft);
      return;
    }

    setQuery('');
  }

  function handleKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    const optionsCount =
      filtered.length + (allowNone ? 1 : 0) + (showCreateOption ? 1 : 0);
    if (!open && (event.key === 'ArrowDown' || event.key === 'Enter')) {
      setOpen(true);
      return;
    }
    if (!open || optionsCount === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((prev) => Math.min(prev + 1, optionsCount - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((prev) => Math.max(prev - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      let idx = highlight;
      if (allowNone) {
        if (idx === 0) {
          selectClient(null);
          return;
        }
        idx -= 1;
      }
      if (showCreateOption) {
        if (idx === 0) {
          commitNewAccountName(trimmedQuery);
          return;
        }
        idx -= 1;
      }
      const client = filtered[idx];
      if (client) selectClient(client);
      else if (allowCreate && trimmedQuery && !queryMatchesAnyClient) {
        commitNewAccountName(trimmedQuery);
      }
    } else if (event.key === 'Escape') {
      isEditingRef.current = false;
      setOpen(false);
      if (selected) {
        setQuery(clientTypeaheadLabel(selected));
      } else if (newAccountDraft.trim()) {
        setQuery(newAccountDraft.trim());
      } else {
        setQuery('');
      }
    }
  }

  return (
    <div ref={rootRef} className="crm-account-typeahead dem-typeahead">
      <input
        ref={inputRef}
        type="text"
        className="field-input crm-account-typeahead__input"
        value={query}
        placeholder={placeholder}
        disabled={disabled}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        role="combobox"
        onFocus={() => {
          isEditingRef.current = true;
          setOpen(true);
          updateListPosition();
        }}
        onChange={(e) => {
          const next = e.target.value;
          isEditingRef.current = true;
          setQuery(next);
          setOpen(true);
          if (selected && next !== clientTypeaheadLabel(selected)) {
            onChange('');
            onNewAccountName?.('');
          }
          if (!next.trim()) {
            onChange('');
            onNewAccountName?.('');
          }
        }}
        onBlur={() => {
          window.setTimeout(() => {
            const active = document.activeElement;
            if (rootRef.current?.contains(active) || listRef.current?.contains(active)) return;
            setOpen(false);
            settleBlur();
          }, 120);
        }}
        onKeyDown={handleKeyDown}
      />
      {open && !disabled
        ? createPortal(
            <ul
              ref={listRef}
              id={listId}
              className="dem-typeahead__list dem-typeahead__list--fixed"
              style={listStyle}
              role="listbox"
            >
              {allowNone ? (
                <li>
                  <button
                    type="button"
                    role="option"
                    aria-selected={highlight === 0}
                    className={`dem-typeahead__option${highlight === 0 ? ' dem-typeahead__option--active' : ''}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectClient(null)}
                  >
                    {noneLabel}
                  </button>
                </li>
              ) : null}
              {showCreateOption ? (
                <li>
                  <button
                    type="button"
                    role="option"
                    aria-selected={highlight === (allowNone ? 1 : 0)}
                    className={`dem-typeahead__option dem-typeahead__option--create${
                      highlight === (allowNone ? 1 : 0) ? ' dem-typeahead__option--active' : ''
                    }`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => commitNewAccountName(trimmedQuery)}
                    onMouseEnter={() => setHighlight(allowNone ? 1 : 0)}
                  >
                    {createOptionLabel(trimmedQuery)}
                  </button>
                </li>
              ) : null}
              {filtered.length === 0 && !showCreateOption ? (
                <li className="dem-typeahead__empty">No matching accounts</li>
              ) : (
                filtered.map((client, index) => {
                  const optionIndex =
                    index + (allowNone ? 1 : 0) + (showCreateOption ? 1 : 0);
                  return (
                    <li key={client.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={optionIndex === highlight}
                        className={`dem-typeahead__option${
                          optionIndex === highlight ? ' dem-typeahead__option--active' : ''
                        }`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectClient(client)}
                        onMouseEnter={() => setHighlight(optionIndex)}
                      >
                        {clientTypeaheadLabel(client)}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}
